// apps/admin/src/api/audit-log-v2.api.ts
//
// Phase 8.3 Wave 5 Task 39 — Audit Log v2 API client (warm + cold tier).
//
// vg-binding: CONTEXT:decisions=P8.D-113,P8.D-138,P8.D-191,P8.D-222,P8.D-258,P8.D-480
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Mirrors the contract published in
//   .vg/phases/08.3-admin-cms-system/API-CONTRACTS/get-api-v1-admin-audit-log.md
// and the live BE plugin at
//   apps/api/src/modules/audit/audit-log.routes.ts
//
// Two endpoints:
//   1. GET /api/v1/admin/audit-log              (warm tier, ≤180 days)
//   2. GET /api/v1/admin/audit-log/archive-search (cold tier, >180 days, S3)
//
// Response envelope per INTERFACE-STANDARDS §response-envelope:
//   { ok:true, data:[AuditLogRow], meta:{...}, request_id? }
// Error envelope per INTERFACE-STANDARDS §error-shape:
//   { ok:false, error:{ code, message, user_message?, field_errors?, request_id } }
//
// FE message priority (per INTERFACE-STANDARDS §message-priority):
//   error.user_message → error.message → message → NETWORK_FALLBACK

import axios from 'axios';

import { API_BASE_URL } from '../lib/apiClient.js';

// ---------------------------------------------------------------------------
// 24 entity types — mirrored from API-CONTRACTS verbatim.
// ---------------------------------------------------------------------------

export const AUDIT_V2_ENTITY_TYPES = [
  'Orders',
  'Users',
  'Products',
  'Finance',
  'Merchants',
  'Vendors',
  'Stores',
  'SupportTickets',
  'Settings',
  'FeatureFlags',
  'KYCDocuments',
  'Payouts',
  'ImpersonationSessions',
  'ApprovalRequests',
  'RoleChanges',
  'APIKeyAccess',
  'PermissionGrants',
  'EmergencyMode',
  'AuditExport',
  'RetentionConfig',
  'CustomRoleCRUD',
  'FeatureFlagChanges',
  'Inventory',
  'MerchantTransferBlock',
] as const;
export type AuditV2EntityType = (typeof AUDIT_V2_ENTITY_TYPES)[number];

export const AUDIT_V2_ACTOR_ROLES = [
  'superadmin',
  'admin',
  'support_admin',
  'accountant',
  'system',
] as const;
export type AuditV2ActorRole = (typeof AUDIT_V2_ACTOR_ROLES)[number];

export const AUDIT_V2_SEVERITY = ['info', 'warning', 'security'] as const;
export type AuditV2Severity = (typeof AUDIT_V2_SEVERITY)[number];

// ---------------------------------------------------------------------------
// Row shape — flat diff key-paths (max depth 3, arrays >10
// collapsed to { collapsed:true, count }).
// ---------------------------------------------------------------------------

export interface AuditV2DiffEntry {
  path: string;
  before: unknown;
  after: unknown;
  collapsed?: boolean;
  count?: number;
}

export interface AuditV2Row {
  id: string;
  event_id: string;
  occurred_at: string;
  actor_user_id: string | null;
  /** #2653: actor email (backfilled server-side); null → UI falls back to id. */
  actor_email: string | null;
  actor_role: string;
  actor_org_type: string | null;
  actor_org_id: string | null;
  action: string;
  target_type: AuditV2EntityType;
  target_id: string;
  diff: AuditV2DiffEntry[];
  diff_truncated: boolean;
  ip: string | null;
  user_agent: string | null;
  severity: AuditV2Severity;
}

export interface AuditV2WarmMeta {
  cursor_next: string | null;
  has_more: boolean;
  tier: 'warm';
  retention_policy: string;
  // Round-14 numbered-pagination fields — present only when the caller
  // requested `?page=`. Undefined for legacy cursor-mode responses.
  page?: number;
  page_count?: number;
  total?: number;
  limit?: number;
}

export interface AuditV2ColdMeta {
  cursor_next: string | null;
  tier: 'cold';
  estimated_seconds: number;
  s3_objects_scanned: number;
  pseudonymized: true;
}

export interface AuditV2WarmResponse {
  rows: AuditV2Row[];
  meta: AuditV2WarmMeta;
  requestId?: string;
}

export interface AuditV2ColdResponse {
  rows: AuditV2Row[];
  meta: AuditV2ColdMeta;
  message?: string;
  requestId?: string;
}

// ---------------------------------------------------------------------------
// Filter shapes — names match the query-string contract (snake_case wire).
// ---------------------------------------------------------------------------

// Round-13 (2026-05-23): `target_type` + `action` upgraded to arrays so the
// admin FE can fire a single multi-select query. BE Zod schema preprocess
// (csvOrArray) accepts both single values + comma-joined arrays + repeated
// keys; the client serialises arrays as comma-joined CSV per the canonical
// shape.
export interface AuditV2WarmFilter {
  actor_user_id?: string;
  actor_role?: AuditV2ActorRole;
  target_type?: AuditV2EntityType[];
  action?: string[];
  date_from?: string;
  date_to?: string;
  severity?: AuditV2Severity;
  q?: string;
  cursor?: string;
  // Round-14: numbered pagination. Mutually exclusive with `cursor`.
  page?: number;
  limit?: number;
}

export interface AuditV2ColdFilter {
  /** ISO8601 — REQUIRED by the cold-tier endpoint contract. */
  date_from: string;
  /** ISO8601 — REQUIRED. */
  date_to: string;
  target_type?: AuditV2EntityType[];
  actor_user_id?: string;
  q?: string;
  cursor?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Error envelope — defensive unwrap mirroring support-tickets.api.ts.
// ---------------------------------------------------------------------------

export interface AuditV2ApiError {
  ok: false;
  code: string;
  /** Toast-ready copy (user_message > message > message > fallback). */
  userMessage: string;
  fieldErrors?: Record<string, string[]>;
  requestId?: string;
}

const NETWORK_FALLBACK = 'Network did not respond — please try again.';

// i18n: callers may pass a bound translator so the network/unknown-shape
// fallback (surfaced as `userMessage` in the error banner) is localised.
// Falls back to the original VI copy when no translator is provided.
type Translate = (key: string, vars?: Record<string, unknown>) => string;

function networkFallback(t?: Translate): string {
  return t ? t('apiMessages.audit.networkFallback') : NETWORK_FALLBACK;
}

function pickString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function unwrapEnvelope<TData, TMeta>(
  payload: unknown,
  t?: Translate,
):
  | { ok: true; data: TData; meta: TMeta; requestId?: string }
  | AuditV2ApiError {
  if (payload == null || typeof payload !== 'object') {
    return {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: networkFallback(t),
    };
  }
  const body = payload as Record<string, unknown>;

  if (body.ok === true && 'data' in body) {
    return {
      ok: true,
      data: body.data as TData,
      meta: (body.meta ?? undefined) as TMeta,
      requestId: pickString(body.request_id),
    };
  }

  const errRaw = body.error;
  if (body.ok === false && errRaw && typeof errRaw === 'object') {
    const err = errRaw as Record<string, unknown>;
    const out: AuditV2ApiError = {
      ok: false,
      code: pickString(err.code) ?? 'UNKNOWN_ERROR',
      userMessage:
        pickString(err.user_message) ??
        pickString(err.message) ??
        pickString(body.message) ??
        networkFallback(t),
    };
    if (err.field_errors && typeof err.field_errors === 'object') {
      out.fieldErrors = err.field_errors as Record<string, string[]>;
    }
    const reqId = pickString(err.request_id) ?? pickString(body.request_id);
    if (reqId) out.requestId = reqId;
    return out;
  }

  return { ok: false, code: 'UNKNOWN_SHAPE', userMessage: networkFallback(t) };
}

const rawAxios = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  // Cold-tier search may take 30s per banner copy — give the request room
  // to land without forcing the caller to plumb its own timeout per call.
  timeout: 45_000,
  headers: { Accept: 'application/json' },
});

function buildQs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.length === 0) continue;
    if (Array.isArray(v)) {
      // Round-13: CSV-join multi-value filters (target_type, action). Empty
      // arrays are skipped so the URL stays clean.
      const joined = v
        .filter((x) => typeof x === 'string' && x.length > 0)
        .join(',');
      if (joined.length === 0) continue;
      sp.set(k, joined);
      continue;
    }
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Warm tier — GET /api/v1/admin/audit-log
// ---------------------------------------------------------------------------

export async function listAuditLogWarm(
  filter: AuditV2WarmFilter,
  t?: Translate,
): Promise<AuditV2WarmResponse> {
  const path = `/api/v1/admin/audit-log${buildQs({ ...filter })}`;
  let body: unknown;
  try {
    const res = await rawAxios.get(path);
    body = res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapEnvelope<unknown, unknown>(err.response.data, t);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: networkFallback(t),
    } satisfies AuditV2ApiError;
  }

  const unwrap = unwrapEnvelope<AuditV2Row[], AuditV2WarmMeta>(body, t);
  if (!unwrap.ok) throw unwrap;

  const rows = Array.isArray(unwrap.data) ? unwrap.data : [];
  const meta: AuditV2WarmMeta = unwrap.meta ?? {
    cursor_next: null,
    has_more: false,
    tier: 'warm',
    retention_policy: '180d warm + S3 cold',
  };
  return { rows, meta, requestId: unwrap.requestId };
}

// ---------------------------------------------------------------------------
// Cold tier — GET /api/v1/admin/audit-log/archive-search
// ---------------------------------------------------------------------------

export async function searchAuditLogArchive(
  filter: AuditV2ColdFilter,
  t?: Translate,
): Promise<AuditV2ColdResponse> {
  const path = `/api/v1/admin/audit-log/archive-search${buildQs({ ...filter })}`;
  let body: unknown;
  let topLevelMessage: string | undefined;
  try {
    const res = await rawAxios.get(path);
    body = res.data;
    if (body && typeof body === 'object') {
      const m = (body as Record<string, unknown>).message;
      if (typeof m === 'string' && m.length > 0) topLevelMessage = m;
    }
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapEnvelope<unknown, unknown>(err.response.data, t);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: networkFallback(t),
    } satisfies AuditV2ApiError;
  }

  const unwrap = unwrapEnvelope<AuditV2Row[], AuditV2ColdMeta>(body, t);
  if (!unwrap.ok) throw unwrap;

  const rows = Array.isArray(unwrap.data) ? unwrap.data : [];
  const meta: AuditV2ColdMeta = unwrap.meta ?? {
    cursor_next: null,
    tier: 'cold',
    estimated_seconds: 30,
    s3_objects_scanned: 0,
    pseudonymized: true,
  };
  const out: AuditV2ColdResponse = { rows, meta, requestId: unwrap.requestId };
  if (topLevelMessage) out.message = topLevelMessage;
  return out;
}
